import { useState } from "react";
import { toast } from "sonner";
import { Car, Plus, Trash2, Zap, X, Tag, Sparkles } from "lucide-react";
import { cn } from "../lib/utils";
import { tripSuggestions, mileageInsights } from "../lib/finance";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useStore } from "../context/StoreContext";
import { fmt, dateLabel, today, uid, rateLabel } from "../lib/format";
import { Section } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";

const blank = () => ({ date: today(), miles: "", purpose: "", from: "", to: "" });

function Templates({ date }) {
  const { state, update, logTrip } = useStore();
  const list = state.tripTemplates || [];
  if (!list.length) return null;
  return (
    <div className="mb-4" data-testid="trip-templates">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" /> Frequent trips — tap to log for {dateLabel(date)}</p>
      <div className="flex flex-wrap gap-2">
        {list.map((t) => (
          <span key={t.id} className="inline-flex items-center rounded-full border bg-card hover:border-primary transition-colors" data-testid={`trip-template-${t.id}`}>
            <button className="pl-3 pr-2 py-1.5 text-sm" onClick={() => { logTrip({ date, miles: t.miles, purpose: t.purpose, from: t.from, templateId: t.id }); toast.success(`Logged ${t.miles} mi — ${t.purpose}`); }} data-testid={`trip-template-log-${t.id}`}>{t.purpose} <span className="num text-muted-foreground">· {t.miles} mi</span></button>
            <button className="px-1 text-muted-foreground hover:text-primary" title={t.matchWords ? `Match words: ${t.matchWords}` : "Add match words"} onClick={() => { const w = window.prompt(`Words that link business expenses to "${t.purpose}" (comma separated):`, t.matchWords || ""); if (w !== null) update("Edit trip match words", (d) => (d.tripTemplates.find((x) => x.id === t.id).matchWords = w.trim())); }} data-testid={`trip-template-words-${t.id}`}><Tag className={cn("h-3.5 w-3.5", t.matchWords && "text-primary")} /></button>
            <button className="pr-2.5 text-muted-foreground hover:text-expense" title="Remove template" onClick={() => update("Remove trip template", (d) => (d.tripTemplates = d.tripTemplates.filter((x) => x.id !== t.id)))} data-testid={`trip-template-delete-${t.id}`}><X className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function TripForm() {
  const { update, logTrip } = useStore();
  const [f, setF] = useState(blank());
  const [keep, setKeep] = useState(false);
  const [words, setWords] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = () => {
    if (!f.date || !(Number(f.miles) > 0)) return toast.error("Enter the date and miles driven");
    if (!f.purpose.trim()) return toast.error("Add the business purpose — it's needed for your records");
    logTrip(f);
    if (keep) update("Save trip template", (d) => (d.tripTemplates = [...(d.tripTemplates || []), { id: uid(), miles: Number(f.miles), purpose: f.purpose.trim(), from: f.from, matchWords: words.trim() }]));
    setF({ ...blank(), date: f.date });
    setKeep(false);
    setWords("");
    toast.success(keep ? "Trip logged and saved as a frequent trip" : "Trip logged");
  };
  return (
    <>
    <Templates date={f.date} />
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2" data-testid="mileage-form">
      <Input type="date" value={f.date} onChange={set("date")} data-testid="mileage-date-input" />
      <Input type="number" min="0" step="0.1" placeholder="Miles" value={f.miles} onChange={set("miles")} className="num" data-testid="mileage-miles-input" />
      <Input placeholder="Business purpose" value={f.purpose} onChange={set("purpose")} className="col-span-2 md:col-span-2" data-testid="mileage-purpose-input" />
      <Input placeholder="From → To (optional)" value={f.from} onChange={set("from")} data-testid="mileage-from-input" />
      <Button onClick={save} data-testid="mileage-add-button"><Plus className="h-4 w-4 mr-1" /> Log trip</Button>
    </div>
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={keep} onCheckedChange={(v) => setKeep(!!v)} data-testid="mileage-save-template-checkbox" /> Save as a frequent trip for one-tap logging</label>
      {keep && <Input placeholder="Match words (optional), e.g. parking, uber, client" value={words} onChange={(e) => setWords(e.target.value)} className="h-9 max-w-sm" data-testid="mileage-match-words-input" />}
    </div>
    </>
  );
}

function Suggestions({ year }) {
  const { state, update, logTrip } = useStore();
  const list = tripSuggestions(state, year);
  if (!list.length) return null;
  return (
    <div className="rounded-xl border border-primary/30 bg-accent/60 p-3 mb-4" data-testid="trip-suggestions">
      <p className="text-sm font-semibold flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-primary" /> Suggested trips <span className="text-xs font-normal text-muted-foreground">from business expenses matching your frequent trips</span></p>
      <ul className="divide-y divide-border/60">
        {list.slice(0, 8).map(({ key, tx, template: p }) => (
          <li key={key} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`trip-suggestion-${tx.id}`}>
            <span className="min-w-0"><b>Log {p.miles} mi – {p.purpose}?</b> <span className="text-muted-foreground">{dateLabel(tx.date)} · {tx.description} ({fmt(Math.abs(tx.amount))})</span></span>
            <span className="flex gap-1.5">
              <Button size="sm" onClick={() => { logTrip({ date: tx.date, miles: p.miles, purpose: p.purpose, from: p.from, txId: tx.id, templateId: p.id }); toast.success(`Logged ${p.miles} mi for ${dateLabel(tx.date)}`); }} data-testid={`trip-suggestion-log-${tx.id}`}>Log</Button>
              <Button size="sm" variant="ghost" onClick={() => update("Dismiss trip suggestion", (d) => (d.dismissedTripSuggestions = [...(d.dismissedTripSuggestions || []), key]))} data-testid={`trip-suggestion-dismiss-${tx.id}`}>Dismiss</Button>
            </span>
          </li>
        ))}
      </ul>
      {list.length > 8 && <p className="text-xs text-muted-foreground mt-1">+{list.length - 8} more</p>}
    </div>
  );
}

function Insights({ year }) {
  const { state } = useStore();
  const { months, byTrip } = mileageInsights(state, year);
  if (!byTrip.length) return null;
  const max = Math.max(...byTrip.map((b) => b.miles), 1);
  return (
    <div className="grid lg:grid-cols-5 gap-4 mb-5" data-testid="mileage-insights">
      <div className="lg:col-span-3 rounded-xl border p-3">
        <p className="text-sm font-semibold mb-2">Business miles by month · {year}</p>
        <div className="h-48" data-testid="mileage-month-chart">
          <ResponsiveContainer>
            <BarChart data={months} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" interval={window.innerWidth < 640 ? 1 : 0} />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} formatter={(v) => [`${v} mi`, "Miles"]} />
              <Bar dataKey="miles" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="lg:col-span-2 rounded-xl border p-3">
        <p className="text-sm font-semibold mb-3">By frequent trip</p>
        <ul className="space-y-2.5" data-testid="mileage-by-trip">
          {byTrip.map((b) => (
            <li key={b.id} data-testid={`mileage-by-trip-${b.id}`}>
              <div className="flex justify-between text-xs mb-1"><span className="font-medium truncate">{b.name}</span><span className="num text-muted-foreground">{b.trips} trip{b.trips === 1 ? "" : "s"} · {b.miles.toLocaleString()} mi · {fmt(b.miles * Number(state.settings.mileageRate ?? 0.7))}</span></div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className={cn("h-full rounded-full", b.id === "_other" ? "bg-muted-foreground/50" : "bg-primary")} style={{ width: `${(b.miles / max) * 100}%` }} /></div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function Mileage({ data, year }) {
  const { update } = useStore();
  return (
    <Section title="Business miles" subtitle={`Trips you drove for work in ${year}. Deduction = miles × ${rateLabel(data.rate)}/mile (change the rate in Settings).`} testid="tax-section-mileage" action={<Car className="h-5 w-5 text-muted-foreground" />}>
      <Insights year={year} />
      <Suggestions year={year} />
      <TripForm />
      <div className="flex flex-wrap gap-2 mb-3">
        <span className="rounded-full border px-3 py-1 text-xs" data-testid="mileage-total-miles">Total: <span className="num">{data.miles.toLocaleString()} mi</span></span>
        <span className="rounded-full border px-3 py-1 text-xs bg-income-soft" data-testid="mileage-total-deduction">Estimated deduction: <span className="num font-semibold">{fmt(data.deduction)}</span></span>
      </div>
      {data.rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]" data-testid="mileage-table">
            <thead className="text-xs uppercase text-muted-foreground border-b"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Purpose</th><th className="p-2 text-left">Route</th><th className="p-2 text-right">Miles</th><th className="p-2 text-right">Deduction</th><th className="p-2" /></tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0" data-testid={`mileage-row-${r.id}`}>
                  <td className="p-2 whitespace-nowrap text-muted-foreground">{dateLabel(r.date)}</td>
                  <td className="p-2">{r.purpose}</td>
                  <td className="p-2 text-xs text-muted-foreground">{r.from || "—"}</td>
                  <td className="p-2 text-right num">{Number(r.miles).toLocaleString()}</td>
                  <td className="p-2 text-right num">{fmt(Number(r.miles) * data.rate)}</td>
                  <td className="p-2 text-right"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => update("Delete trip", (d) => (d.mileage = d.mileage.filter((x) => x.id !== r.id)))} data-testid={`mileage-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm text-muted-foreground" data-testid="mileage-empty">No trips logged for {year}. Log each business drive with its purpose — commuting to your regular workplace usually doesn't count.</p>}
    </Section>
  );
}
